// cncForm.ts — CNC 견적 폼 상태(selection) ↔ 계산 입력/결과 변환 (순수).
// 계산 공식은 @/lib/cost/cncQuote 를 재사용한다 (재구현 금지).
import { GENERAL_ADMIN_RATE_INHOUSE, GENERAL_ADMIN_RATE_OUTSOURCED } from "@/lib/cost/admin";
import { machineExpense } from "@/lib/cost/cncExpense";
import {
  type CncQuoteInput,
  type CncQuoteResult,
  type CncTreatmentInput,
  DEFAULT_SCRAP_RECOVERY_RATE,
  amortizedMoldCost,
  cncQuote,
} from "@/lib/cost/cncQuote";
import { allowanceForProcess } from "@/lib/cost/labor";
import { DEFAULT_REMNANT_LENGTH, DEFAULT_STOCK_LENGTH } from "@/lib/cost/weightCalc";
import type {
  CncLaborRate,
  CncMachineExpense,
  CncPartSelection,
  CncRawMaterial,
  CncSettings,
  CncSurfaceTreatment,
  CncTreatmentLine,
} from "@/lib/data";

export interface CncMasterData {
  raw: CncRawMaterial[];
  surface: CncSurfaceTreatment[];
  labor: CncLaborRate[];
  expense: CncMachineExpense[];
  settings: CncSettings;
}

// 문자열 입력 → 숫자 (빈 값/비수치는 0).
const num = (s: string): number => Number.parseFloat(s) || 0;

export function emptyCncSelection(settings: CncSettings): CncPartSelection {
  return {
    itemName: "",
    partNo: "",
    spec: "",
    partQuoteType: "",
    // 기본 시작 연도 2027 (고객 요청 — 물량정보는 2027년부터 세팅).
    volumes: { y2027: "", y2028: "", y2029: "", y2030: "", y2031: "" },
    usgQty: "",
    materialId: "",
    outerDiameter: "",
    productLength: "",
    inputWeight: "",
    productWeight: "",
    rawPrice: "0",
    scrapPrice: "0",
    scrapWeight: "",
    // 새 견적은 회수율 기본 90% (재질 선택 시 재질별 값으로 교체) — 내용추가18.
    scrapRecoveryRate: String(DEFAULT_SCRAP_RECOVERY_RATE),
    stockLength: String(DEFAULT_STOCK_LENGTH),
    remnantLength: String(DEFAULT_REMNANT_LENGTH),
    rows: [],
    surfaceLines: [],
    heatLines: [],
    etcLines: [],
    profitMode: "amount",
    profit: "0",
    profitRate: "0",
    costType: "inhouse",
    moldTotal: "0",
    amortize: true,
    productionQty: "0",
    miscCost: "0",
    allowance: settings.allowance,
    generalAdminRate: settings.generalAdminRate,
    materialAdminRate: settings.materialAdminRate,
  };
}

// 내작/외작 → 일반관리비율 (엑셀 CNC1 Q24: 내작 17% / 외작 10%).
export function generalAdminRateForCostType(costType: CncPartSelection["costType"]): number {
  return costType === "inhouse" ? GENERAL_ADMIN_RATE_INHOUSE : GENERAL_ADMIN_RATE_OUTSOURCED;
}

// 폼 라인(문자열 단가) → 계산 입력 라인.
const toTreatmentInput = (line: CncTreatmentLine): CncTreatmentInput => ({
  unit: line.unit,
  unitPrice: num(line.unitPrice),
});

// 공정명 → 기계경비(₩/hr). laborRateId 의 공정명에 매칭되는 경비 row 의 machineExpense 결과.
export function machineRateForLabor(laborRateId: string, master: CncMasterData): number {
  const lr = master.labor.find((r) => r.id === laborRateId);
  if (!lr) return 0;
  const exp = master.expense.find((e) => e.process === lr.process);
  return exp ? machineExpense(exp).hourlyRate : 0;
}

// 행의 laborRateId → 공정명. master 미전달이거나 매칭 row 없으면 "".
export function processNameOf(laborRateId: string, master?: CncMasterData): string {
  return master?.labor.find((r) => r.id === laborRateId)?.process ?? "";
}

// ----- 스크랩 회수율 (내용추가18) -----
//
// 고객 확정: 재질마다 다르되 기본 90%, 견적에서 수정 가능, "새로 쓰는 견적부터만" 적용.
// → 회수율 필드가 없는 selection(도입 전 저장 견적)은 100% 로 계산해 금액을 바꾸지 않는다.

// 견적의 회수율(%) 입력(sel.scrapRecoveryRate) → 계산용 분수. 필드 없음(기존 견적)/빈 값 = 1(100%).
export function scrapRecoveryFraction(raw: string | undefined): number {
  if (raw == null || raw.trim() === "") return 1;
  return num(raw) / 100;
}

// 재질의 기본 회수율(%) — 마스터 값이 없으면 90.
export function materialRecoveryRate(m: Pick<CncRawMaterial, "recoveryRate"> | undefined): number {
  return m?.recoveryRate ?? DEFAULT_SCRAP_RECOVERY_RATE;
}

// 재질 선택 시 견적에 반영할 패치 — 원소재/스크랩 단가 + 재질 회수율.
// 회수율 도입 전 견적(필드 없음)은 재질을 바꿔도 회수율을 새로 넣지 않는다(기존 견적 100% 유지).
// 재질을 찾지 못하면(마스터 미로딩 등) 재질 id 만 바꾸고 기존 값을 보존한다.
export function materialSelectionPatch(
  sel: CncPartSelection,
  materialId: string,
  master: Pick<CncMasterData, "raw">
): Partial<CncPartSelection> {
  const m = master.raw.find((r) => r.id === materialId);
  if (!m) return { materialId };
  return {
    materialId,
    rawPrice: String(m.rawUnitPrice),
    scrapPrice: String(m.scrapUnitPrice),
    ...(sel.scrapRecoveryRate !== undefined
      ? { scrapRecoveryRate: String(materialRecoveryRate(m)) }
      : {}),
  };
}

// 견적 복사(새 견적) — 원본이 회수율 도입 전 견적이면 그 재질의 기본 회수율을 채운다.
// (복사본은 "새로 쓰는 견적"이므로 회수율 적용 대상.)
export function asNewQuoteSelection(
  sel: CncPartSelection,
  master: Pick<CncMasterData, "raw">
): CncPartSelection {
  if (sel.scrapRecoveryRate !== undefined) return sel;
  const m = master.raw.find((r) => r.id === sel.materialId);
  return { ...sel, scrapRecoveryRate: String(materialRecoveryRate(m)) };
}

// ----- 투입중량 계산 팝업 적용 (내용추가18) -----

export interface InputWeightApply {
  inputWeight: string; // 메인 저장값 — 소수 둘째 자리 버림(9.69)
  outerDiameter: string;
  materialId: string;
  productLength: string;
  stockLength: string; // 원자재 전장 (mm) — 견적별 저장
  remnantLength: string; // 잔봉 길이 (mm) — 견적별 저장
  rawPrice?: string; // 원소재 단가 (₩/g) — 팝업 원자재 원가 수정/재질 변경 시만. undefined = 유지
}

// 팝업 결과를 견적 selection 에 병합한다.
//   - 단가/회수율 자동입력은 "재질이 바뀐 경우"에만(내용추가11: 수기 수정 단가가 마스터값으로
//     되돌아가는 버그 방지).
//   - 팝업에서 원자재 원가(원/kg)를 고쳤으면 메인 원소재 단가(₩/g)도 그 값으로 바꾼다.
export function applyInputWeightResult(
  sel: CncPartSelection,
  r: InputWeightApply,
  master: Pick<CncMasterData, "raw">
): CncPartSelection {
  const patch =
    r.materialId !== sel.materialId ? materialSelectionPatch(sel, r.materialId, master) : {};
  return {
    ...sel,
    ...patch,
    materialId: r.materialId,
    outerDiameter: r.outerDiameter,
    productLength: r.productLength,
    stockLength: r.stockLength,
    remnantLength: r.remnantLength,
    inputWeight: r.inputWeight,
    ...(r.rawPrice !== undefined ? { rawPrice: r.rawPrice } : {}),
  };
}

export function selectionToInput(sel: CncPartSelection, master?: CncMasterData): CncQuoteInput {
  // 스크랩중량 — 입력값이 있으면 그대로, 미입력(빈 문자열)이면 투입−제품 자동(기존 동작 호환).
  const scrapWeightG =
    sel.scrapWeight.trim() !== ""
      ? num(sel.scrapWeight)
      : num(sel.inputWeight) - num(sel.productWeight);
  return {
    material: {
      inputWeightG: num(sel.inputWeight),
      productWeightG: num(sel.productWeight),
      rawUnitPrice: num(sel.rawPrice),
      scrapUnitPrice: num(sel.scrapPrice),
      scrapWeightG,
      scrapRecoveryRate: scrapRecoveryFraction(sel.scrapRecoveryRate),
      surfaceLines: sel.surfaceLines.map(toTreatmentInput),
      heatLines: sel.heatLines.map(toTreatmentInput),
      etcLines: sel.etcLines.map(toTreatmentInput),
    },
    processes: sel.rows.map((row) => {
      // 공정명 — master 가 있으면 laborRateId 로 resolve(여유율 미적용 공정 식별 키). 라벨은 컴포넌트가 부여.
      const processName = processNameOf(row.laborRateId, master);
      const fallback = row.allowance ?? sel.allowance; // 공정별 여유율 — 구 스냅샷 fallback
      return {
        processName,
        ctSec: num(row.ctSec),
        headcount: row.headcount,
        hourlyLaborRate: row.hourlyLaborRate,
        machineHourlyRate: num(row.machineRate),
        // 최종검사/포장은 공정명으로 0 강제, 그 외는 fallback. (#79)
        allowance: allowanceForProcess(processName, fallback),
      };
    }),
    profit: num(sel.profit),
    // 마진율 모드(판매가 기준): 제품원가 = 내부원가 ÷ (1 − 마진율). cncQuote 가 내부원가를 알므로 분수만 전달.
    profitRate: sel.profitMode === "rate" ? num(sel.profitRate) / 100 : undefined,
    moldCost: sel.amortize
      ? amortizedMoldCost(num(sel.moldTotal), num(sel.productionQty))
      : num(sel.moldTotal),
    miscCost: num(sel.miscCost),
    generalAdminRate: sel.generalAdminRate,
    materialAdminRate: sel.materialAdminRate,
  };
}

export function selectionToResult(sel: CncPartSelection, master?: CncMasterData): CncQuoteResult {
  return cncQuote(selectionToInput(sel, master));
}
